import { gql } from '@apollo/client';
import { useMutation, useQuery } from '@apollo/client/react';
import { InfoIcon, Loader2Icon } from 'lucide-react';
import {
  Fragment,
  type PropsWithChildren,
  useEffect,
  useMemo,
  useState
} from 'react';
import { useNavigate, useParams } from 'react-router';
import { toast } from 'sonner';

import Header from '../../Header';
import { RightSidebarWrapper } from '../../RightSidebar/RightSidebarWrapper';
import {
  CREATE_RISK_SCORECARD_HISTORY,
  type CreateRiskScorecardHistoryResponse,
  GET_RISK_SCORECARD,
  type GetRiskScorecardResponse,
  type RiskScorecardInput,
  UPDATE_RISK_SCORECARD,
  type UpdateRiskScorecardResponse
} from '../graphql';

import NumberField from '@/components/NumberField';
import RichTextFieldReadOnly from '@/components/RichTextFieldReadOnly';
import { Button } from '@/components/ui/button';
import { Field, FieldLabel } from '@/components/ui/field';
import {
  InputGroup,
  InputGroupAddon,
  InputGroupText
} from '@/components/ui/input-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { Spinner } from '@/components/ui/spinner';
import { Textarea } from '@/components/ui/textarea';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from '@/components/ui/tooltip';

const SectionHeader = ({
  children,
  title
}: PropsWithChildren<{ title: string }>) => (
  <div className="mb-2 flex items-center justify-between gap-3">
    <h2 className="text-lg font-semibold">{title}</h2>
    {children && <div className="flex items-center gap-2">{children}</div>}
  </div>
);

const Card = ({ children }: PropsWithChildren) => (
  <div className="bg-card rounded-md border p-5">{children}</div>
);

const InfoTooltipIcon = ({ text }: { text: string }) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <Button
        aria-label="More info"
        className="h-4 w-4 p-0"
        size="icon-sm"
        type="button"
        variant="ghost"
      >
        <InfoIcon className="size-4" />
      </Button>
    </TooltipTrigger>
    <TooltipContent className="max-w-64">{text}</TooltipContent>
  </Tooltip>
);

const MetricItem = ({
  label,
  tooltip,
  value
}: {
  label: string;
  tooltip?: string;
  value: string;
}) => (
  <div className="flex items-center justify-between gap-4">
    <div className="flex items-center gap-2 text-sm font-normal">
      <span>{label}</span>
      {tooltip && <InfoTooltipIcon text={tooltip} />}
    </div>
    <div className="text-muted-foreground text-sm font-medium">{value}</div>
  </div>
);

const PlaceholderSelect = ({
  id,
  onValueChange,
  options,
  placeholder,
  value
}: {
  id: string;
  onValueChange?: (value: string) => void;
  options: { label: string; value: string }[];
  placeholder: string;
  value?: string;
}) => (
  <Select
    onValueChange={onValueChange}
    value={value ?? ''}
  >
    <SelectTrigger
      className="w-full"
      id={id}
    >
      <SelectValue placeholder={placeholder} />
    </SelectTrigger>
    <SelectContent>
      {options.map(option => (
        <SelectItem
          key={option.value}
          value={option.value}
        >
          {option.label}
        </SelectItem>
      ))}
    </SelectContent>
  </Select>
);

const RequiredMark = () => <span className="text-destructive">{' *'}</span>;

const useOfProceedsOptions = [
  { label: 'Dividend Recap', value: 'Dividend Recap' },
  { label: 'Refinancing', value: 'Refinancing' },
  { label: 'Change of Control Buyout', value: 'Change of Control Buyout' },
  { label: 'Acquisition', value: 'Acquisition' },
  { label: 'Growth Capital', value: 'Growth Capital' }
];

const revenueModelOptions = [
  { label: 'Recurring', value: 'Recurring' },
  { label: 'Re-Occurring', value: 'Re-Occurring' },
  { label: 'Project Based', value: 'Project Based' }
];

const businessTypeOptions = [
  { label: 'Service', value: 'Service' },
  { label: 'Product', value: 'Product' }
];

const industryResilienceOptions = [
  { label: '0 of Recession Resiliency or Tailwinds', value: '1' },
  { label: '1 of Recession Resiliency or Tailwinds', value: '3' },
  { label: 'Strong Tailwinds and Recession Resiliency', value: '5' }
];

const marketAttributesOptions = [
  { label: '0', value: '0' },
  { label: '1', value: '1' },
  { label: '2', value: '2' },
  { label: '3', value: '3' },
  { label: '4', value: '4' }
];

const sponsorManagementOptions = [
  { label: '0', value: '0' },
  { label: '1', value: '1' },
  { label: '2', value: '2' },
  { label: '3', value: '3' },
  { label: '4', value: '4' },
  { label: '5', value: '5' }
];

const roundTo = (value: number, precision: number): number => {
  const factor = 10 ** precision;
  return Math.round((value + Number.EPSILON) * factor) / factor;
};

const GET_MARKET_POSITION_READ_ONLY = gql`
  query MarketPositionReadOnly($deal_id: ID!) {
    companyMarketPositionByDeal(dealOpportunityId: $deal_id) {
      id
      businessCriticalityCommentary
      barriersToEntryDescription
      competitiveAdvantageDescription
      marketLeaderDescription
    }
  }
`;

const ReadOnlyBlock = ({ label, value }: { label: string; value: string }) => (
  <div className="space-y-2">
    <div className="text-sm font-medium">{label}</div>
    <RichTextFieldReadOnly html={value || ''} />
  </div>
);

interface MarketPositionReadOnlyResponse {
  companyMarketPositionByDeal: {
    barriersToEntryDescription: null | string;
    businessCriticalityCommentary: null | string;
    competitiveAdvantageDescription: null | string;
    id: string;
    marketLeaderDescription: null | string;
  };
}

const InvestmentsDirectIdDealScoreAddScore = ({
  hideRightSidebar = false
}: {
  hideRightSidebar?: boolean;
}) => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data, error, loading } = useQuery<GetRiskScorecardResponse>(
    GET_RISK_SCORECARD,
    {
      skip: !id,
      variables: { id }
    }
  );

  const { data: marketPositionData } = useQuery<
    MarketPositionReadOnlyResponse,
    { deal_id: string }
  >(GET_MARKET_POSITION_READ_ONLY, {
    skip: !id,
    variables: { deal_id: id! }
  });

  const marketPosition = marketPositionData?.companyMarketPositionByDeal;

  const [updateScorecard] = useMutation<UpdateRiskScorecardResponse>(
    UPDATE_RISK_SCORECARD
  );
  const [createHistory, { loading: creatingHistory }] =
    useMutation<CreateRiskScorecardHistoryResponse>(
      CREATE_RISK_SCORECARD_HISTORY
    );

  const scorecard = data?.node;

  const [draft, setDraft] = useState<RiskScorecardInput>({});

  useEffect(() => {
    if (scorecard) {
      const toPercent = (v: null | number) =>
        v != null ? roundTo(v * 100, 6) : undefined;
      setDraft({
        businessModelSynthesis: scorecard.businessModelSynthesis ?? '',
        businessType: scorecard.businessType ?? '',
        dealStructureSynthesis: scorecard.dealStructureSynthesis ?? '',
        ebitdaCapexInterest1: scorecard.ebitdaCapexInterest1 ?? undefined,
        ebitdaMargins: toPercent(scorecard.ebitdaMargins),
        equityAsPercentOfTotalCapital: toPercent(
          scorecard.equityAsPercentOfTotalCapital
        ),
        fcfConversionPercentage: toPercent(scorecard.fcfConversionPercentage),
        industryResilience: scorecard.industryResilience ?? '',
        industryResilienceSynthesis:
          scorecard.industryResilienceSynthesis ?? '',
        interestCoverageRatio: scorecard.interestCoverageRatio ?? undefined,
        leverageAttachmentPoint: scorecard.leverageAttachmentPoint ?? '',
        leverageDetachmentPoint: scorecard.leverageDetachmentPoint ?? '',
        marketAttributesCount: scorecard.marketAttributesCount ?? undefined,
        marketAttributesSynthesis: scorecard.marketAttributesSynthesis ?? '',
        operatingCashFlowSynthesis: scorecard.operatingCashFlowSynthesis ?? '',
        organicRevenueCagr: toPercent(scorecard.organicRevenueCagr),
        organicRevenueGrowth: toPercent(scorecard.organicRevenueGrowth),
        productGrossMarginThresholds: toPercent(
          scorecard.productGrossMarginThresholds
        ),
        revenueModel: scorecard.revenueModel ?? '',
        serviceGrossMarginThresholds: toPercent(
          scorecard.serviceGrossMarginThresholds
        ),
        sponsorManagementScore: scorecard.sponsorManagementScore ?? undefined,
        sponsorManagementSynthesis: scorecard.sponsorManagementSynthesis ?? '',
        useOfProceeds: scorecard.useOfProceeds ?? ''
      });
    }
  }, [scorecard]);

  const isScoreDisabled = useMemo(() => {
    return (
      !draft.equityAsPercentOfTotalCapital ||
      !draft.useOfProceeds ||
      !draft.industryResilience ||
      draft.sponsorManagementScore == null
    );
  }, [draft]);

  const handleScore = async () => {
    if (!id) return;

    try {
      const pctToDecimal = (v: number | undefined) =>
        v != null ? roundTo(v / 100, 10) : undefined;
      const values = {
        ...draft,
        ebitdaMargins: pctToDecimal(draft.ebitdaMargins),
        equityAsPercentOfTotalCapital: pctToDecimal(
          draft.equityAsPercentOfTotalCapital
        ),
        fcfConversionPercentage: pctToDecimal(draft.fcfConversionPercentage),
        organicRevenueCagr: pctToDecimal(draft.organicRevenueCagr),
        organicRevenueGrowth: pctToDecimal(draft.organicRevenueGrowth),
        productGrossMarginThresholds: pctToDecimal(
          draft.productGrossMarginThresholds
        ),
        serviceGrossMarginThresholds: pctToDecimal(
          draft.serviceGrossMarginThresholds
        )
      };
      const updateResponse = await updateScorecard({
        variables: {
          id,
          values
        }
      });

      if (!updateResponse.data?.updateOpportunityRiskScorecard.ok) {
        throw new Error('Failed to update scorecard');
      }

      const historyResponse = await createHistory({
        refetchQueries: [{ query: GET_RISK_SCORECARD, variables: { id } }],
        variables: {
          values: {
            opportunityId: id
          }
        }
      });

      if (!historyResponse.data?.createRiskScorecardHistory.ok) {
        throw new Error('Failed to create score history');
      }

      toast.success('Score saved successfully');
      void navigate(`/investments/direct/${id}/deal-score`);
    } catch {
      toast.error('Unable to save score');
    }
  };

  if (loading) {
    return (
      <div className="-mt-16 grid h-[calc(100%+64px)] place-content-center">
        <Spinner className="size-8 text-blue-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-destructive p-5">
        Error loading scorecard: {error.message}
      </div>
    );
  }

  const Wrapper = hideRightSidebar ? Fragment : RightSidebarWrapper;

  return (
    <>
      <Header>
        <Button
          disabled={isScoreDisabled || creatingHistory}
          onClick={() => void handleScore()}
          type="button"
        >
          {creatingHistory && <Loader2Icon className="animate-spin" />}
          Score
        </Button>
      </Header>
      <Wrapper>
        <div className="space-y-7 px-5 pt-5 pb-16.5 md:pl-14">
          <section>
            <SectionHeader title="A. Deal Structure" />
            <Card>
              <div className="space-y-5">
                <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
                  <MetricItem
                    label="Net Attachment Point"
                    tooltip="Computed from security structure"
                    value={scorecard?.leverageAttachmentPointCalc ?? '-'}
                  />
                  <MetricItem
                    label="Net Detachment Point"
                    tooltip="Computed from security structure"
                    value={scorecard?.leverageDetachmentPointCalc ?? '-'}
                  />
                </div>
                <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
                  <Field>
                    <FieldLabel htmlFor="deal-structure-equity-total">
                      Equity as % of Total
                      <RequiredMark />
                    </FieldLabel>
                    <InputGroup>
                      <NumberField
                        id="deal-structure-equity-total"
                        onChange={e => {
                          setDraft(prev => ({
                            ...prev,
                            equityAsPercentOfTotalCapital: e.target.value
                              ? parseFloat(e.target.value.replace(/,/g, ''))
                              : undefined
                          }));
                        }}
                        placeholder="Enter Value"
                        value={draft.equityAsPercentOfTotalCapital ?? ''}
                      />
                      <InputGroupAddon align="inline-end">
                        <InputGroupText>%</InputGroupText>
                      </InputGroupAddon>
                    </InputGroup>
                  </Field>
                  <Field>
                    <FieldLabel htmlFor="deal-structure-use-of-proceeds">
                      Use of Proceeds
                      <RequiredMark />
                    </FieldLabel>
                    <PlaceholderSelect
                      id="deal-structure-use-of-proceeds"
                      onValueChange={value => {
                        setDraft(prev => ({ ...prev, useOfProceeds: value }));
                      }}
                      options={useOfProceedsOptions}
                      placeholder="Select an option"
                      value={draft.useOfProceeds}
                    />
                  </Field>
                </div>
                <Field>
                  <FieldLabel htmlFor="deal-structure-synthesis">
                    Synthesis
                  </FieldLabel>
                  <Textarea
                    id="deal-structure-synthesis"
                    onChange={e => {
                      setDraft(prev => ({
                        ...prev,
                        dealStructureSynthesis: e.target.value
                      }));
                    }}
                    placeholder="Enter synthesis notes"
                    value={draft.dealStructureSynthesis ?? ''}
                  />
                </Field>
              </div>
            </Card>
          </section>
          <section>
            <SectionHeader title="B. Business Model" />
            <Card>
              <div className="space-y-5">
                <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
                  <Field>
                    <FieldLabel htmlFor="business-model-revenue-model">
                      Revenue Model
                    </FieldLabel>
                    <PlaceholderSelect
                      id="business-model-revenue-model"
                      onValueChange={value => {
                        setDraft(prev => ({ ...prev, revenueModel: value }));
                      }}
                      options={revenueModelOptions}
                      placeholder="Select an option"
                      value={draft.revenueModel}
                    />
                  </Field>
                  <MetricItem
                    label="Top 10 Customer Concentration"
                    tooltip="Pulled from Company Overview"
                    value={
                      scorecard?.top10CustomerConcentrationScore != null
                        ? `Score: ${scorecard.top10CustomerConcentrationScore.toString()}`
                        : '-'
                    }
                  />
                </div>
                <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
                  <Field>
                    <FieldLabel htmlFor="business-model-organic-revenue-cagr">
                      3-Year Organic Revenue CAGR
                    </FieldLabel>
                    <InputGroup>
                      <NumberField
                        id="business-model-organic-revenue-cagr"
                        onChange={e => {
                          setDraft(prev => ({
                            ...prev,
                            organicRevenueCagr: e.target.value
                              ? parseFloat(e.target.value.replace(/,/g, ''))
                              : undefined
                          }));
                        }}
                        placeholder="Enter Value"
                        value={draft.organicRevenueCagr ?? ''}
                      />
                      <InputGroupAddon align="inline-end">
                        <InputGroupText>%</InputGroupText>
                      </InputGroupAddon>
                    </InputGroup>
                  </Field>
                  <Field>
                    <FieldLabel htmlFor="business-model-organic-revenue-growth">
                      1-Year Organic Revenue Growth
                    </FieldLabel>
                    <InputGroup>
                      <NumberField
                        id="business-model-organic-revenue-growth"
                        onChange={e => {
                          setDraft(prev => ({
                            ...prev,
                            organicRevenueGrowth: e.target.value
                              ? parseFloat(e.target.value.replace(/,/g, ''))
                              : undefined
                          }));
                        }}
                        placeholder="Enter Value"
                        value={draft.organicRevenueGrowth ?? ''}
                      />
                      <InputGroupAddon align="inline-end">
                        <InputGroupText>%</InputGroupText>
                      </InputGroupAddon>
                    </InputGroup>
                  </Field>
                </div>
                <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
                  <Field>
                    <FieldLabel htmlFor="business-type">
                      Business Type
                    </FieldLabel>
                    <PlaceholderSelect
                      id="business-type"
                      onValueChange={value => {
                        setDraft(prev => ({ ...prev, businessType: value }));
                      }}
                      options={businessTypeOptions}
                      placeholder="Select type"
                      value={draft.businessType}
                    />
                  </Field>
                  {draft.businessType === 'Service' ? (
                    <Field>
                      <FieldLabel htmlFor="service-gross-margin">
                        Service Gross Margin
                      </FieldLabel>
                      <InputGroup>
                        <NumberField
                          id="service-gross-margin"
                          onChange={e => {
                            setDraft(prev => ({
                              ...prev,
                              serviceGrossMarginThresholds: e.target.value
                                ? parseFloat(e.target.value.replace(/,/g, ''))
                                : undefined
                            }));
                          }}
                          placeholder="Enter Value"
                          value={draft.serviceGrossMarginThresholds ?? ''}
                        />
                        <InputGroupAddon align="inline-end">
                          <InputGroupText>%</InputGroupText>
                        </InputGroupAddon>
                      </InputGroup>
                    </Field>
                  ) : draft.businessType === 'Product' ? (
                    <Field>
                      <FieldLabel htmlFor="product-gross-margin">
                        Product Gross Margin
                      </FieldLabel>
                      <InputGroup>
                        <NumberField
                          id="product-gross-margin"
                          onChange={e => {
                            setDraft(prev => ({
                              ...prev,
                              productGrossMarginThresholds: e.target.value
                                ? parseFloat(e.target.value.replace(/,/g, ''))
                                : undefined
                            }));
                          }}
                          placeholder="Enter Value"
                          value={draft.productGrossMarginThresholds ?? ''}
                        />
                        <InputGroupAddon align="inline-end">
                          <InputGroupText>%</InputGroupText>
                        </InputGroupAddon>
                      </InputGroup>
                    </Field>
                  ) : (
                    <div />
                  )}
                </div>
                <Field>
                  <FieldLabel htmlFor="business-model-synthesis">
                    Synthesis
                  </FieldLabel>
                  <Textarea
                    id="business-model-synthesis"
                    onChange={e => {
                      setDraft(prev => ({
                        ...prev,
                        businessModelSynthesis: e.target.value
                      }));
                    }}
                    placeholder="Enter synthesis notes"
                    value={draft.businessModelSynthesis ?? ''}
                  />
                </Field>
              </div>
            </Card>
          </section>
          <section>
            <SectionHeader title="C. Operating Cash Flow" />
            <Card>
              <div className="space-y-5">
                <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
                  <Field>
                    <FieldLabel htmlFor="operating-cash-flow-ebitda">
                      EBITDA Margins
                    </FieldLabel>
                    <InputGroup>
                      <NumberField
                        id="operating-cash-flow-ebitda"
                        onChange={e => {
                          setDraft(prev => ({
                            ...prev,
                            ebitdaMargins: e.target.value
                              ? parseFloat(e.target.value.replace(/,/g, ''))
                              : undefined
                          }));
                        }}
                        placeholder="Enter Value"
                        value={draft.ebitdaMargins ?? ''}
                      />
                      <InputGroupAddon align="inline-end">
                        <InputGroupText>%</InputGroupText>
                      </InputGroupAddon>
                    </InputGroup>
                  </Field>
                  <Field>
                    <FieldLabel htmlFor="operating-cash-flow-ebitda-capex-interest">
                      (EBITDA - CapEx) / Interest
                    </FieldLabel>
                    <InputGroup>
                      <NumberField
                        id="operating-cash-flow-ebitda-capex-interest"
                        onChange={e => {
                          setDraft(prev => ({
                            ...prev,
                            ebitdaCapexInterest1: e.target.value
                              ? parseFloat(e.target.value.replace(/,/g, ''))
                              : undefined
                          }));
                        }}
                        placeholder="Enter Value"
                        value={draft.ebitdaCapexInterest1 ?? ''}
                      />
                      <InputGroupAddon align="inline-end">
                        <InputGroupText>x</InputGroupText>
                      </InputGroupAddon>
                    </InputGroup>
                  </Field>
                </div>
                <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
                  <Field>
                    <FieldLabel htmlFor="operating-cash-flow-interest-coverage-ratio">
                      Interest Coverage Ratio
                    </FieldLabel>
                    <InputGroup>
                      <NumberField
                        id="operating-cash-flow-interest-coverage-ratio"
                        onChange={e => {
                          setDraft(prev => ({
                            ...prev,
                            interestCoverageRatio: e.target.value
                              ? parseFloat(e.target.value.replace(/,/g, ''))
                              : undefined
                          }));
                        }}
                        placeholder="Enter Value"
                        value={draft.interestCoverageRatio ?? ''}
                      />
                      <InputGroupAddon align="inline-end">
                        <InputGroupText>x</InputGroupText>
                      </InputGroupAddon>
                    </InputGroup>
                  </Field>
                  <Field>
                    <FieldLabel htmlFor="operating-cash-flow-fcf-conversion">
                      FCF Conversion
                    </FieldLabel>
                    <InputGroup>
                      <NumberField
                        id="operating-cash-flow-fcf-conversion"
                        onChange={e => {
                          setDraft(prev => ({
                            ...prev,
                            fcfConversionPercentage: e.target.value
                              ? parseFloat(e.target.value.replace(/,/g, ''))
                              : undefined
                          }));
                        }}
                        placeholder="Enter Value"
                        value={draft.fcfConversionPercentage ?? ''}
                      />
                      <InputGroupAddon align="inline-end">
                        <InputGroupText>%</InputGroupText>
                      </InputGroupAddon>
                    </InputGroup>
                  </Field>
                </div>
                <Field>
                  <FieldLabel htmlFor="operating-cash-flow-synthesis">
                    Synthesis
                  </FieldLabel>
                  <Textarea
                    id="operating-cash-flow-synthesis"
                    onChange={e => {
                      setDraft(prev => ({
                        ...prev,
                        operatingCashFlowSynthesis: e.target.value
                      }));
                    }}
                    placeholder="Enter synthesis notes"
                    value={draft.operatingCashFlowSynthesis ?? ''}
                  />
                </Field>
              </div>
            </Card>
          </section>
          <section>
            <SectionHeader title="D. Market Position" />
            <Card>
              <div className="space-y-5">
                <div className="grid grid-cols-1 gap-5 @md:grid-cols-2">
                  <div className="space-y-4">
                    <ReadOnlyBlock
                      label="Business Criticality"
                      value={
                        marketPosition?.businessCriticalityCommentary ?? ''
                      }
                    />
                    <ReadOnlyBlock
                      label="Competitive Advantages"
                      value={
                        marketPosition?.competitiveAdvantageDescription ?? ''
                      }
                    />
                  </div>
                  <div className="space-y-4">
                    <ReadOnlyBlock
                      label="Barriers to Entry"
                      value={marketPosition?.barriersToEntryDescription ?? ''}
                    />
                    <ReadOnlyBlock
                      label="Market Leader"
                      value={marketPosition?.marketLeaderDescription ?? ''}
                    />
                  </div>
                </div>
                <Field>
                  <FieldLabel htmlFor="market-position-attributes-count">
                    How many Market Attributes does the Company Exhibit?
                  </FieldLabel>
                  <PlaceholderSelect
                    id="market-position-attributes-count"
                    onValueChange={value => {
                      setDraft(prev => ({
                        ...prev,
                        marketAttributesCount: parseInt(value, 10)
                      }));
                    }}
                    options={marketAttributesOptions}
                    placeholder="Select count"
                    value={draft.marketAttributesCount?.toString()}
                  />
                </Field>
                <Field>
                  <FieldLabel htmlFor="market-position-synthesis">
                    Synthesis
                  </FieldLabel>
                  <Textarea
                    id="market-position-synthesis"
                    onChange={e => {
                      setDraft(prev => ({
                        ...prev,
                        marketAttributesSynthesis: e.target.value
                      }));
                    }}
                    placeholder="Enter synthesis notes"
                    value={draft.marketAttributesSynthesis ?? ''}
                  />
                </Field>
              </div>
            </Card>
          </section>
          <section>
            <SectionHeader title="E. Industry Resilience" />
            <Card>
              <div className="space-y-5">
                <Field>
                  <FieldLabel htmlFor="industry-resilience-attributes-count">
                    Recession Resiliency / Industry Tailwinds
                    <RequiredMark />
                  </FieldLabel>
                  <PlaceholderSelect
                    id="industry-resilience-attributes-count"
                    onValueChange={value => {
                      setDraft(prev => ({
                        ...prev,
                        industryResilience: value
                      }));
                    }}
                    options={industryResilienceOptions}
                    placeholder="Select an option"
                    value={draft.industryResilience}
                  />
                </Field>
                <Field>
                  <FieldLabel htmlFor="industry-resilience-synthesis">
                    Synthesis
                  </FieldLabel>
                  <Textarea
                    id="industry-resilience-synthesis"
                    onChange={e => {
                      setDraft(prev => ({
                        ...prev,
                        industryResilienceSynthesis: e.target.value
                      }));
                    }}
                    placeholder="Enter synthesis notes"
                    value={draft.industryResilienceSynthesis ?? ''}
                  />
                </Field>
              </div>
            </Card>
          </section>
          <section>
            <SectionHeader title="F. Sponsor / Management" />
            <Card>
              <div className="space-y-5">
                <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
                  <Field>
                    <FieldLabel htmlFor="sponsor-management-score">
                      Score
                      <RequiredMark />
                    </FieldLabel>
                    <PlaceholderSelect
                      id="sponsor-management-score"
                      onValueChange={value => {
                        setDraft(prev => ({
                          ...prev,
                          sponsorManagementScore: parseInt(value, 10)
                        }));
                      }}
                      options={sponsorManagementOptions}
                      placeholder="Select score"
                      value={draft.sponsorManagementScore?.toString()}
                    />
                  </Field>
                </div>
                <Field>
                  <FieldLabel htmlFor="sponsor-management-synthesis">
                    Synthesis
                  </FieldLabel>
                  <Textarea
                    id="sponsor-management-synthesis"
                    onChange={e => {
                      setDraft(prev => ({
                        ...prev,
                        sponsorManagementSynthesis: e.target.value
                      }));
                    }}
                    placeholder="Enter synthesis notes"
                    value={draft.sponsorManagementSynthesis ?? ''}
                  />
                </Field>
              </div>
            </Card>
          </section>
        </div>
      </Wrapper>
    </>
  );
};

export default InvestmentsDirectIdDealScoreAddScore;
